// Authenticator-app codes (#227): TOTP after RFC 6238 (HMAC-SHA1, 30-second steps, 6 digits), the base32 keys people
// type into their app, and the one-time recovery codes. Web Crypto only, so it runs in Workers, Node and the browser.

import { encode as encodeQr } from 'uqr'

export const TOTP_PERIOD_S = 30
export const TOTP_DIGITS = 6
/** Codes from one step before or after now are accepted too: phones drift. */
export const TOTP_WINDOW = 1
export const RECOVERY_CODE_COUNT = 10

const B32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'

export function base32Encode(bytes: Uint8Array): string {
  let out = ''
  let bits = 0
  let value = 0
  for (const b of bytes) {
    value = (value << 8) | b
    bits += 8
    while (bits >= 5) {
      out += B32[(value >>> (bits - 5)) & 31]
      bits -= 5
    }
  }
  if (bits > 0) out += B32[(value << (5 - bits)) & 31]
  return out
}

/** Spaces, dashes, padding and case are ignored (people copy keys in groups). Null for anything else. */
export function base32Decode(text: string): Uint8Array<ArrayBuffer> | null {
  const clean = text.replace(/[\s-]/g, '').replace(/=+$/, '').toUpperCase()
  const out: number[] = []
  let bits = 0
  let value = 0
  for (const ch of clean) {
    const i = B32.indexOf(ch)
    if (i < 0) return null
    value = (value << 5) | i
    bits += 5
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 255)
      bits -= 8
    }
  }
  return Uint8Array.from(out)
}

/** RFC 4226 HOTP for one counter value. */
export async function hotp(key: Uint8Array<ArrayBuffer>, counter: number, digits = TOTP_DIGITS): Promise<string> {
  const msg = new Uint8Array(8)
  new DataView(msg.buffer).setBigUint64(0, BigInt(counter))
  const hmacKey = await crypto.subtle.importKey('raw', key, { name: 'HMAC', hash: 'SHA-1' }, false, ['sign'])
  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', hmacKey, msg))
  const offset = mac[mac.length - 1]! & 0x0f
  const bin = ((mac[offset]! & 0x7f) << 24) | (mac[offset + 1]! << 16) | (mac[offset + 2]! << 8) | mac[offset + 3]!
  return String(bin % 10 ** digits).padStart(digits, '0')
}

/** The time step a moment falls in. */
export const totpStep = (nowMs: number) => Math.floor(nowMs / 1000 / TOTP_PERIOD_S)

/** The code an app shows at `nowMs` for a base32 key. */
export async function totpCode(secret: string, nowMs = Date.now()): Promise<string> {
  const key = base32Decode(secret)
  if (!key) throw new Error('Not a base32 key')
  return hotp(key, totpStep(nowMs))
}

/** Six digits, with spaces allowed in between (apps show "123 456"). */
export const looksLikeTotp = (code: string) => /^\d{6}$/.test(code.replace(/\s/g, ''))

/**
 * The time step a code belongs to (now, or one step either side), or null. The caller refuses a step it has already
 * accepted, so the same code can't be used twice.
 */
export async function matchTotp(secret: string, code: string, nowMs = Date.now()): Promise<number | null> {
  const given = code.replace(/\s/g, '')
  const key = base32Decode(secret)
  if (!key || !/^\d{6}$/.test(given)) return null
  const now = totpStep(nowMs)
  let found: number | null = null
  // Every step is computed and compared in full, so the time taken doesn't say which one matched.
  for (let step = now - TOTP_WINDOW; step <= now + TOTP_WINDOW; step++) {
    const expected = await hotp(key, step)
    let diff = 0
    for (let i = 0; i < TOTP_DIGITS; i++) diff |= expected.charCodeAt(i) ^ given.charCodeAt(i)
    if (diff === 0 && found === null) found = step
  }
  return found
}

/** A new key: 20 random bytes (160 bits, the RFC's recommendation for SHA-1), base32. */
export const newTotpSecret = () => base32Encode(crypto.getRandomValues(new Uint8Array(20)))

/** The key in groups of four, easier to type into an app by hand. */
export const groupKey = (secret: string) => secret.replace(/(.{4})(?=.)/g, '$1 ')

/** What the QR code holds: the otpauth:// address every authenticator app reads. */
export function otpauthUri(input: { issuer: string; account: string; secret: string }) {
  const issuer = encodeURIComponent(input.issuer)
  const label = `${issuer}:${encodeURIComponent(input.account)}`
  return `otpauth://totp/${label}?secret=${input.secret}&issuer=${issuer}&algorithm=SHA1&digits=${TOTP_DIGITS}&period=${TOTP_PERIOD_S}`
}

// Recovery codes: ten of them, shown once. 10 characters from 31 letters and digits without look-alikes (49 bits),
// written as two groups of five. Only their SHA-256 is stored.
const RECOVERY_ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789'

function recoveryCode() {
  let s = ''
  while (s.length < 10) {
    for (const b of crypto.getRandomValues(new Uint8Array(16))) {
      // 248 = 8 × 31: bytes above it are skipped, so every character is equally likely.
      if (b < 248 && s.length < 10) s += RECOVERY_ALPHABET[b % RECOVERY_ALPHABET.length]
    }
  }
  return `${s.slice(0, 5)}-${s.slice(5)}`
}

export const newRecoveryCodes = (count = RECOVERY_CODE_COUNT): string[] => Array.from({ length: count }, recoveryCode)

/** Case, spaces and the dash don't matter when a recovery code is typed. */
export const normalizeRecoveryCode = (code: string) => code.toLowerCase().replace(/[\s-]/g, '')

const toHex = (buf: ArrayBuffer) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('')

export const hashRecoveryCode = async (code: string) => toHex(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(normalizeRecoveryCode(code))))

/**
 * The QR code of an otpauth address as one SVG path (made here, never by an outside service). `size` is the number of
 * modules across, quiet zone included; draw it with viewBox `0 0 size size`.
 */
export function qrPath(text: string): { size: number; d: string } {
  const qr = encodeQr(text, { ecc: 'M', border: 2 })
  let d = ''
  qr.data.forEach((row, y) => {
    let x = 0
    while (x < row.length) {
      if (!row[x]) {
        x++
        continue
      }
      const start = x
      while (x < row.length && row[x]) x++
      d += `M${start} ${y}h${x - start}v1h${start - x}z`
    }
  })
  return { size: qr.size, d }
}

/** The same QR code as a complete <svg> element, for pages rendered on the server. */
export function qrSvg(text: string, title: string): string {
  const { size, d } = qrPath(text)
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;')
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" role="img" aria-label="${esc(title)}" shape-rendering="crispEdges"><rect width="${size}" height="${size}" fill="#fff"/><path fill="#000" d="${d}"/></svg>`
}

// The rollout switch (#227), one per Worker: TWO_FACTOR = "off" | "grace" | "enforce".
export type TwoFactorMode = 'off' | 'grace' | 'enforce'

/** Anything but "grace" or "enforce" is off: exactly the sign-in there was before #227. */
export const twoFactorMode = (value: string | undefined): TwoFactorMode => (value === 'grace' || value === 'enforce' ? value : 'off')

/**
 * What sign-in asks for after a correct password: `code` from anyone who has set up 2FA (while the switch isn't off),
 * `setup` from someone who must have it but hasn't (enforce only), otherwise nothing more.
 */
export function secondStep(mode: TwoFactorMode, required: boolean, enabled: boolean): 'none' | 'code' | 'setup' {
  if (mode === 'off') return 'none'
  if (enabled) return 'code'
  return required && mode === 'enforce' ? 'setup' : 'none'
}

/** Grace period: someone who will have to use 2FA, and hasn't set it up, sees a banner asking them to. */
export const twoFactorNudge = (mode: TwoFactorMode, required: boolean, enabled: boolean) => mode !== 'off' && required && !enabled
